/**
 * A refund in the till's electronic journal.
 *
 * The refund pages run in the till's WebView. When a refund is done, the page tells the till what was refunded and how
 * it was paid back, and the till writes a return receipt (returkvittering) with its own number and a return (SAF-T
 * 11006) in its electronic journal: the lines, their VAT, the money paid out and the cash rounding (TastyPOS
 * ReturnJournal, JSBridge.journalRefund). Until 2026-10-26 a refund wrote only the event that a return receipt was made.
 *
 * Sent once per refund, where the till's grand total of returns is counted (incrementGrandTotalRefund), with the money
 * that went back: a leg that failed is left out. Not from Backoffice in a browser: there is no till there, and a refund
 * SUPER_ADMIN makes from Backoffice is journaled by the server (VivaTerminalRefund).
 *
 * Amounts are worked in øre, so the lines, the tips and the money paid back add up to the øre.
 */
class RefundJournal {

  /**
   * @param {object} refund
   * @param {object} refund.order the order refunded
   * @param {string} refund.orderDocumentId its document id
   * @param {object[]} refund.rows the refunded rows of the page, one per unit or share
   * @param {function} refund.priceOf (row) => what the row was refunded at, VAT included
   * @param {number} [refund.linesTotal] what the rows came to together after every discount, the order's too, when the
   *        page has not put the order's discount on the rows: spread over them in proportion. Default: the rows' sum.
   * @param {object[]} refund.paidBack [{paymentType, amount, transactionId}]: each leg that went through, amount positive
   * @param {number} [refund.tips] the tips paid back
   * @param {number} [refund.planned] what the refund was to pay back, when it is paid back in legs: if one failed, the
   *        lines and the tips are taken in proportion to what did go back
   * @param {number|null} [refund.saleReceiptNr] the sale's receipt number; default the order's. Null where the order's
   *        number is not the sale this refund returns (one guest's payment of several).
   */
  send(refund) {
    if(!(window.JSBridge && window.JSBridge.journalRefund)) {
      return; // not in a till, or in a till from before the journal took refunds
    }
    const journal = this.build(refund);
    if(!journal) {
      return;
    }
    try {
      window.JSBridge.journalRefund(JSON.stringify(journal));
    } catch (error) {
      console.log('journalRefund failed', error); // the till says so itself when it cannot journal it
    }
  }

  /** What the till is sent; null when nothing went back. */
  build({ order, orderDocumentId, rows, priceOf, linesTotal, paidBack, tips = 0, planned, saleReceiptNr }) {
    const legs = (paidBack || []).filter(leg => leg && toOre(leg.amount) > 0);
    const paid = legs.reduce((sum, leg) => sum + toOre(leg.amount), 0);
    if(paid === 0) {
      return null;
    }
    const prices = (rows || []).map(row => Math.max(0, toOre(priceOf(row))));
    const meant = toOre(planned);
    let tipsOre;
    let linesOre;
    if(meant > paid) {
      // A leg failed: what did go back, its tips in proportion and the rest the lines. No rounding: the cash, which
      // the rounding is on, is paid last.
      tipsOre = Math.round(toOre(tips) * paid / meant);
      linesOre = paid - tipsOre;
    } else {
      tipsOre = toOre(tips);
      linesOre = linesTotal === undefined || linesTotal === null
          ? prices.reduce((sum, price) => sum + price, 0)
          : toOre(linesTotal);
    }
    const lines = spread(prices, Math.max(0, linesOre));
    const receiptNr = saleReceiptNr === undefined ? order.saleReceiptNr : saleReceiptNr;
    return {
      orderDocumentId,
      orderId: order.orderId,
      orderType: order.orderType,
      saleReceiptNr: receiptNr > 0 ? receiptNr : null,
      lines: (rows || []).map((row, i) => ({
        id: row.id,
        itemId: row.itemId,
        itemName: row.itemName, // as the page shows it, «Vin (1/3)»: the till prints it on the return receipt
        itemType: row.itemType,
        count: 1,
        price: lines[i] / 100,
        ...(row.shareDenominator > 0 ? {
          shareNumerator: row.shareNumerator || 1,
          shareDenominator: row.shareDenominator,
          shareOfCount: row.shareOfCount || 1,
          shareAmount: lines[i] / 100,
        } : {}),
      })),
      payments: legs.map(leg => ({
        paymentType: leg.paymentType,
        amount: toOre(leg.amount) / 100,
        transactionId: leg.transactionId || null,
      })),
      tips: tipsOre / 100,
    };
  }

  /** The card payment a refund pays back, as the order holds it: its type and its transaction. */
  cardLeg(order, transactionId, amount) {
    const payment = (order.payments || []).find(p => p.transactionId && transactionId
        && String(p.transactionId).toLowerCase() === String(transactionId).toLowerCase());
    return { paymentType: payment ? payment.paymentType : 'CARD', amount, transactionId };
  }
}

function toOre(kroner) {
  return Math.round(((Number(kroner) || 0) + Number.EPSILON) * 100);
}

/**
 * Øre spread over the rows in proportion to their prices, the remainder to the largest fractions, so they add up to
 * the total exactly.
 */
export function spread(prices, total) {
  if(prices.length === 0) {
    return [];
  }
  const sum = prices.reduce((a, b) => a + b, 0);
  if(sum === 0) {
    return prices.map((price, i) => (i === 0 ? total : 0));
  }
  const exact = prices.map(price => price * total / sum);
  const out = exact.map(Math.floor);
  let left = total - out.reduce((a, b) => a + b, 0);
  const byFraction = exact.map((value, i) => ({ i, fraction: value - out[i] }))
      .sort((a, b) => b.fraction - a.fraction || a.i - b.i);
  for(let k = 0; left > 0; k = (k + 1) % byFraction.length, left--) {
    out[byFraction[k].i] += 1;
  }
  return out;
}

export default new RefundJournal();
